// frontend/shared/src/table.ts —— TD-820：代理 / 后台共用的服务端分页表格状态机
// 字段与原型同名：qd → draft、qa → applied、pageNo / pageSize / sortKey / sortDir / lastReq / loading / searching
import type { AdminPage } from './api/types';
import { pageWindow, reqLine, type ListQuery } from './page';

export interface Draft {
  kw: string;
  from: string;
  to: string;
  f: Readonly<Record<string, string>>;
}

const EMPTY: Draft = Object.freeze({ kw: '', from: '', to: '', f: Object.freeze({}) });

export interface TableOptions<T> {
  endpoint: string; // 回显用，如 '/agent/users'
  fetch: (q: ListQuery) => Promise<AdminPage<T>>;
  minLoadingMs: number; // 代理 400 / 后台 420
  searchingMs: number; // 原型 520
  onChange?: () => void;
}

export class TableState<T> {
  draft: Draft = EMPTY;
  applied: Draft = EMPTY;
  tab = '';
  pageNo = 1;
  pageSize = 10;
  sortKey: string | null = null;
  sortDir: 'asc' | 'desc' = 'desc';
  lastReq = '';
  loading = false;
  searching = false;
  error = false;
  rows: T[] = [];
  total = 0;
  facetOptions: Readonly<Record<string, string[]>> = {};
  private seq = 0;
  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly o: TableOptions<T>) {}

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  get pages(): number[] {
    return pageWindow(this.pageNo, this.totalPages);
  }

  query(): ListQuery {
    return {
      page: this.pageNo,
      size: this.pageSize,
      keyword: this.applied.kw,
      facets: this.applied.f,
      start: this.applied.from,
      end: this.applied.to,
      tab: this.tab,
      ...(this.sortKey ? { sort: this.sortKey, order: this.sortDir } : {}),
    };
  }

  setDraft(d: Partial<Draft>): void {
    this.draft = { ...this.draft, ...d, f: { ...this.draft.f, ...(d.f ?? {}) } };
    this.o.onChange?.();
  }

  runQuery(): Promise<void> {
    this.applied = { ...this.draft, f: { ...this.draft.f } };
    this.pageNo = 1;
    this.searching = true;
    this.lastReq = reqLine(this.o.endpoint, this.query());
    // 连续点查询时以最后一次为准重新计时，避免前一次的定时器提前收起「查询中」
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.searchTimer = null;
      this.searching = false;
      this.o.onChange?.();
    }, this.o.searchingMs);
    return this.reload();
  }

  resetQuery(): Promise<void> {
    this.draft = EMPTY;
    this.applied = EMPTY;
    this.pageNo = 1;
    this.lastReq = '';
    return this.reload();
  }

  setTab(tab: string): Promise<void> {
    this.tab = tab;
    this.pageNo = 1;
    this.draft = EMPTY;
    this.applied = EMPTY;
    this.lastReq = '';
    return this.reload();
  }

  toggleSort(key: string): Promise<void> {
    this.sortDir = this.sortKey === key && this.sortDir === 'desc' ? 'asc' : 'desc';
    this.sortKey = key;
    this.pageNo = 1;
    return this.reload();
  }

  setPageSize(n: number): Promise<void> {
    this.pageSize = n === 20 || n === 50 ? n : 10;
    this.pageNo = 1;
    return this.reload();
  }

  goPage(n: number): Promise<void> {
    this.pageNo = Math.min(Math.max(1, Math.trunc(n) || 1), this.totalPages);
    if (this.lastReq) this.lastReq = reqLine(this.o.endpoint, this.query());
    return this.reload();
  }

  // 竞态：只采用最后一次请求（seq）的结果；失败清空行并置 error，不向调用方抛
  async reload(): Promise<void> {
    const my = ++this.seq;
    this.loading = true;
    this.error = false;
    this.o.onChange?.();
    const started = Date.now();
    try {
      const r = await this.o.fetch(this.query());
      const wait = this.o.minLoadingMs - (Date.now() - started);
      if (wait > 0) await new Promise((res) => setTimeout(res, wait));
      if (my !== this.seq) return;
      this.rows = r.list;
      this.total = r.total;
      this.facetOptions = r.facet_options ?? {};
    } catch (e) {
      if (my !== this.seq) return;
      console.error('[gp:table]', this.o.endpoint, e);
      this.rows = [];
      this.total = 0;
      this.error = true;
    } finally {
      if (my === this.seq) {
        this.loading = false;
        this.o.onChange?.();
      }
    }
  }
}
